import { useState, useEffect, Key, useRef } from 'react'
import cx from 'classnames'
import Styles from './ConfigureReviewAutomationModal.module.scss'
import { ReviewerRole, ReviewType, ReviewPrivacy } from 'components/ReviewsWizard/types'
import { roleProps, REQUESTED_TYPE, DueDateDays, reviewerRolesFromUsers, DEFAULT_DURATION } from './utils'
import { ReviewersFields } from 'components/ReviewsWizard/AddReviewers/ReviewersFields'
import { useRootStore } from 'stores/RootStore'
import { Value } from 'components/ShareSheet/RecipientSelector/Types'
import { ReviewTemplate, RecipientsSelectorValueType } from 'types'
import { observer } from 'mobx-react-lite'
import { CreateReviewActionConfig } from 'stores/Workflows/Types'
import {
  parseReviewers,
  dehydrateValues,
  hydrateValues,
  Reviewer,
} from 'components/CreateReviewActionConfigModal/utils'
import { useSourceData } from '../ReviewsWizard/SourceDataProvider/useSourceData'
import { SourceDataProvider } from '../ReviewsWizard/SourceDataProvider/SourceDataProvider'
import { ModalPromptProps } from 'hooks/useActionModalPrompt'
import { startCase } from 'lodash'
import { Steps } from './Steps'
import { AddMoreRoles } from './AddMoreRoles'
import { TemplatesDropdown } from './TemplatesDropdown'
import { type PropertyValue } from 'api/clients/PropertyClient'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { MessageField } from 'components/MessageField/MessageField'
import { ProseInputHandle } from 'common/ProseInput/Types'
import { Modal } from 'almanac-ui/Modal'

export type Props = ModalPromptProps<
  Partial<CreateReviewActionConfig>,
  {
    reviewType: ReviewType
    fromStatus: Pick<PropertyValue, 'value' | 'meta'>
    toStatus: Pick<PropertyValue, 'value' | 'meta'>
  }
>

export const ConfigureReviewAutomationModal = observer(
  ({
    reviewers: initialReviewers = [],
    approvers: initialApprovers = [],
    viewers: initialViewers = [],
    channel_reviewers: initialChannelReviewers = [],
    channel_approvers: initialChannelApprovers = [],
    channel_viewers: initialChannelViewers = [],
    reviewType: initialReviewType,
    onConfirm,
    html_description: initialHTMLDescription = '',
    duration: initialDuration = parseInt(DEFAULT_DURATION),
    privacy: initialPrivacy = ReviewPrivacy.PUBLIC,
    fromStatus,
    toStatus,
    onClose,
  }: Props) => {
    const rootStore = useRootStore()
    const inputRef = useRef<ProseInputHandle>(null)
    const [templateId, setTemplateId] = useState<number>()
    useEffect(() => {
      ;[...initialReviewers, ...initialApprovers, ...initialViewers].forEach(rootStore.users.fetch)
      ;[...initialChannelReviewers, ...initialChannelApprovers, ...initialChannelViewers].forEach(
        rootStore.channels.fetch,
      )
    }, [rootStore]) // eslint-disable-line react-hooks/exhaustive-deps

    const [reviewType, setReviewType] = useState<ReviewType>(initialReviewType)
    const [selectedPrivacyType, setSelectedPrivacyType] = useState<ReviewPrivacy>(initialPrivacy)
    const [htmlMessage, setHtmlMessage] = useState<string>(initialHTMLDescription)
    const [duration, setDuration] = useState(initialDuration.toString())
    const [customDuration, setCustomDuration] = useState<string>(DEFAULT_DURATION)
    const [reviewerRoles, setReviewerRoles] = useState(() =>
      reviewerRolesFromUsers(
        reviewType,
        [...initialReviewers, ...initialChannelReviewers],
        [...initialApprovers, ...initialChannelApprovers],
        [...initialViewers, ...initialChannelViewers],
      ),
    )

    const [reviewers, setReviewers] = useState<Reviewer[]>(() =>
      parseReviewers(initialReviewers, initialChannelReviewers),
    )
    const [approvers, setApprovers] = useState<Reviewer[]>(() =>
      parseReviewers(initialApprovers, initialChannelApprovers),
    )
    const [viewers, setViewers] = useState<Reviewer[]>(() => parseReviewers(initialViewers, initialChannelViewers))

    const setReviewerValues = (values: Value[]) => setReviewers(dehydrateValues(values, rootStore))
    const setApproverValues = (values: Value[]) => setApprovers(dehydrateValues(values, rootStore))
    const setViewerValues = (values: Value[]) => setViewers(dehydrateValues(values, rootStore))

    const sourceData = useSourceData({})
    const includeGroups = true
    const includeSelf = true

    const durationIsValid =
      duration !== DueDateDays.CUSTOM || (duration === DueDateDays.CUSTOM && !isNaN(parseInt(customDuration)))

    const saveConfiguration = () => {
      if (!durationIsValid) return

      let newDuration
      if (duration !== undefined && duration !== DueDateDays.CUSTOM) {
        newDuration = parseInt(duration)
      } else if (customDuration !== undefined) {
        newDuration = parseInt(customDuration)
      } else {
        newDuration = parseInt(DEFAULT_DURATION)
      }

      onConfirm({
        reviewers: reviewers
          .filter((reviewer) => reviewer.type === RecipientsSelectorValueType.User)
          .map((reviewer) => reviewer.value) as number[],
        channel_reviewers: reviewers
          .filter((reviewer) => reviewer.type === RecipientsSelectorValueType.Channel)
          .map((reviewer) => reviewer.value) as number[],
        approvers: approvers
          .filter((approver) => approver.type === RecipientsSelectorValueType.User)
          .map((approver) => approver.value) as number[],
        channel_approvers: approvers
          .filter((approver) => approver.type === RecipientsSelectorValueType.Channel)
          .map((approver) => approver.value) as number[],
        viewers: viewers
          .filter((viewer) => viewer.type === RecipientsSelectorValueType.User)
          .map((viewer) => viewer.value) as number[],
        channel_viewers: viewers
          .filter((viewer) => viewer.type === RecipientsSelectorValueType.Channel)
          .map((viewer) => viewer.value) as number[],
        privacy: selectedPrivacyType,
        html_description: htmlMessage,
        duration: newDuration,
      })
      onClose()
    }

    const handleAction = (action: Key) => {
      switch (action) {
        case 'reviewers':
          if (!reviewerRoles.includes(ReviewerRole.REVIEWER)) {
            setReviewerRoles((roles) => [...roles, ReviewerRole.REVIEWER])
          }
          break
        case 'approvers':
          if (!reviewerRoles.includes(ReviewerRole.APPROVER)) {
            setReviewerRoles((roles) => [...roles, ReviewerRole.APPROVER])
          }
          break
        case 'viewers':
          if (!reviewerRoles.includes(ReviewerRole.VIEWER)) {
            setReviewerRoles((roles) => [...roles, ReviewerRole.VIEWER])
          }
          break
      }
    }

    const handleTemplateSelect = (template: ReviewTemplate) => {
      setTemplateId(template.id)
      const newReviewers = template.user_reviewers.map((user) => user.id)
      const newApprovers = template.user_approvers.map((user) => user.id)
      const newViewers = template.user_viewers.map((user) => user.id)
      const newChannelReviewers = template.channel_reviewers.map((channel) => channel.id)
      const newChannelApprovers = template.channel_approvers.map((channel) => channel.id)
      const newChannelViewers = template.channel_viewers.map((channel) => channel.id)

      ;[...newReviewers, ...newApprovers, ...newViewers].forEach(rootStore.users.fetch)
      ;[...newChannelReviewers, ...newChannelApprovers, ...newChannelViewers].forEach(rootStore.channels.fetch)

      const roles = reviewerRolesFromUsers(
        reviewType,
        [...newReviewers, ...newChannelReviewers],
        [...newApprovers, ...newChannelApprovers],
        [...newViewers, ...newChannelViewers],
      )
      setReviewerRoles(roles)

      setReviewers(parseReviewers(newReviewers, newChannelReviewers))
      setApprovers(parseReviewers(newApprovers, newChannelApprovers))
      setViewers(parseReviewers(newViewers, newChannelViewers))

      setSelectedPrivacyType(template.privacy)
      setDuration(template.days_ahead.toString())

      const templateHtmlMessage = template.html_description || template.description
      setHtmlMessage(templateHtmlMessage)
      inputRef.current?.replaceHTMLString(templateHtmlMessage)
    }

    const handleDefaultTemplateSelect = (reviewType: ReviewType) => {
      setTemplateId(undefined)
      setReviewType(reviewType)
      setReviewers([])
      setApprovers([])
      setViewers([])

      setReviewerRoles(reviewerRolesFromUsers(reviewType))
      setSelectedPrivacyType(ReviewPrivacy.PUBLIC)
      setHtmlMessage('')
      inputRef.current?.replaceHTMLString('')
      setDuration(DueDateDays.THREE)
    }

    return (
      <>
        <Modal.Root size="md" onPressOutside={onClose}>
          <Modal.Header
            onClose={onClose}
            title={`Configure ${startCase(reviewType)} Automation`}
            actions={
              <TemplatesDropdown
                onTemplateSelect={handleTemplateSelect}
                onDefaultTemplateSelect={handleDefaultTemplateSelect}
                selectedKey={templateId?.toString()}
                showManageAction={false}
              />
            }
          />
          <Modal.Content>
            <SourceDataProvider sourceData={() => sourceData(includeGroups, includeSelf)}>
              <div className={Styles.reviewersFieldsWrapper}>
                <ReviewersFields
                  {...roleProps(reviewType)}
                  reviewers={hydrateValues(reviewers, rootStore)}
                  onReviewersChange={setReviewerValues}
                  approvers={hydrateValues(approvers, rootStore)}
                  onApproversChange={setApproverValues}
                  viewers={hydrateValues(viewers, rootStore)}
                  onViewersChange={setViewerValues}
                  roles={reviewerRoles}
                  allowEmails={false}
                  placeholder="Start typing a name..."
                  recipientsClassNames={Styles.recipients}
                  reviewerRolesDropdown={<AddMoreRoles reviewerRoles={reviewerRoles} handleAction={handleAction} />}
                />
              </div>
            </SourceDataProvider>
            <div className={cx(Styles.label, Styles.messageFieldLabel)}>
              Automatic message to appear in the {REQUESTED_TYPE[reviewType]} request
            </div>
            <MessageField
              value={htmlMessage}
              onChange={setHtmlMessage}
              placeholder="Add a message you can modify each time you start the automation..."
              ref={inputRef}
            />
            <div className={cx(Styles.label, Styles.explanationLabel)}>How the automation will work</div>
            <Steps
              duration={duration}
              onDurationChange={setDuration}
              onCustomDurationChange={setCustomDuration}
              customDuration={customDuration}
              fromStatus={fromStatus}
              toStatus={toStatus}
            />
          </Modal.Content>
          <Modal.Footer>
            <Modal.FooterContent slot="left">
              <Modal.FooterButton variant="tertiary" size="lg" onPress={onClose}>
                Back
              </Modal.FooterButton>
            </Modal.FooterContent>
            <Modal.FooterContent slot="right">
              <Tooltip label="Please select a valid due date" hide={durationIsValid}>
                <Modal.FooterButton variant="primary" size="lg" onPress={saveConfiguration}>
                  Save Configuration
                </Modal.FooterButton>
              </Tooltip>
            </Modal.FooterContent>
          </Modal.Footer>
        </Modal.Root>
      </>
    )
  },
)
